import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getConflicts,
  getCrew,
  getEquipment,
  getFestival,
  listCrewAssignments,
  type FestivalListItem,
  type RiderListItem,
} from "@/api/client";
import { DashboardHeroUpload } from "@/components/dashboard/DashboardHeroUpload";
import { usePermission } from "@/hooks/usePermission";
import { useAuthStore } from "@/stores/auth";
import { resolveStageColor } from "@/lib/scheduleLayout";
import {
  AlertTriangle,
  ArrowRight,
  Boxes,
  Calendar,
  CheckCircle2,
  ChevronRight,
  FileText,
  Loader2,
  MapPin,
  PlayCircle,
  Route,
  Upload,
  Users,
  type LucideIcon,
} from "lucide-react";

/**
 * Dashboard — operator console for the active organization. The page answers
 * three questions fast: how ready is the next festival, what needs action now,
 * and where should production staff go next.
 */

interface Props {
  festivals: FestivalListItem[];
  riders: RiderListItem[];
  layoutSwitch?: ReactNode;
}

type StatusTone = "ok" | "warn" | "bad" | "info" | "neutral";
type ChartView = "readiness" | "stages";

const TONE_COLOR: Record<StatusTone, string> = {
  ok: "var(--tr-ok)",
  warn: "var(--tr-warn)",
  bad: "var(--tr-bad)",
  info: "var(--tr-info)",
  neutral: "var(--tr-text-4)",
};

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function daysUntil(dateStr: string): number {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [ty, tm, td] = todayStr().split("-").map(Number);
  const target = Date.UTC(y!, m! - 1, d!);
  const today = Date.UTC(ty!, tm! - 1, td!);
  return Math.round((target - today) / 86_400_000);
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function pct(value: number): number {
  return Math.round(clamp01(value) * 100);
}

function toneForScore(value: number): StatusTone {
  if (value >= 0.85) return "ok";
  if (value >= 0.6) return "warn";
  return "bad";
}

export function DashboardNext({ festivals, riders, layoutSwitch }: Props) {
  const user = useAuthStore((s) => s.user);
  const firstName = user?.name?.split(" ")[0] ?? "there";
  const [chartView, setChartView] = useState<ChartView>("readiness");

  const today = todayStr();
  const hero = useMemo(() => {
    const ongoing = festivals.filter(
      (f) => f.start_date <= today && today <= f.end_date,
    );
    if (ongoing.length > 0) return ongoing[0]!;
    const upcoming = [...festivals]
      .filter((f) => f.start_date > today)
      .sort((a, b) => a.start_date.localeCompare(b.start_date));
    return upcoming[0] ?? festivals[0] ?? null;
  }, [festivals, today]);
  const heroLive = !!hero && hero.start_date <= today && today <= hero.end_date;

  const heroDetailQuery = useQuery({
    queryKey: ["festival", hero?.id],
    queryFn: () => getFestival(hero!.id),
    enabled: !!hero,
    placeholderData: (previous) => previous,
  });

  const { allowed: conflictsAllowed } = usePermission("conflict_detector");
  const conflictsQuery = useQuery({
    queryKey: ["conflicts", hero?.id],
    queryFn: () => getConflicts(hero!.id),
    enabled: !!hero && conflictsAllowed,
    retry: false,
    placeholderData: (previous) => previous,
  });

  const { allowed: equipmentAllowed } = usePermission("equipment_basic");
  const equipmentQuery = useQuery({
    queryKey: ["equipment", hero?.id],
    queryFn: () => getEquipment(hero!.id),
    enabled: !!hero && equipmentAllowed,
    retry: false,
    placeholderData: (previous) => previous,
  });

  const { allowed: crewAllowed } = usePermission("crew_calculator");
  const crewQuery = useQuery({
    queryKey: ["crew", hero?.id],
    queryFn: () => getCrew(hero!.id),
    enabled: !!hero && crewAllowed,
    retry: false,
    placeholderData: (previous) => previous,
  });

  const crewAssignmentsQuery = useQuery({
    queryKey: ["crew", "assignments", hero?.id, "dashboard"],
    queryFn: () => listCrewAssignments(hero!.id),
    enabled: !!hero && crewAllowed,
    retry: false,
    placeholderData: (previous) => previous,
  });

  // The detail modules resolve at different speeds. Debounce publication
  // until every enabled query has settled, retaining the last coherent set
  // during background refreshes instead of repainting each metric separately.
  const [detailSnapshot, setDetailSnapshot] = useState<{
    heroId: string;
    heroDetail: typeof heroDetailQuery.data;
    conflicts: typeof conflictsQuery.data;
    equipment: typeof equipmentQuery.data;
    crew: typeof crewQuery.data;
    crewAssignments: NonNullable<typeof crewAssignmentsQuery.data>;
  } | null>(null);

  const detailQueriesSettled =
    !hero ||
    (!heroDetailQuery.isFetching &&
      (!conflictsAllowed || !conflictsQuery.isFetching) &&
      (!equipmentAllowed || !equipmentQuery.isFetching) &&
      (!crewAllowed || (!crewQuery.isFetching && !crewAssignmentsQuery.isFetching)));

  useEffect(() => {
    if (!hero || !detailQueriesSettled || heroDetailQuery.data === undefined) return;
    const timer = window.setTimeout(() => {
      setDetailSnapshot({
        heroId: hero.id,
        heroDetail: heroDetailQuery.data,
        conflicts: conflictsQuery.data,
        equipment: equipmentQuery.data,
        crew: crewQuery.data,
        crewAssignments: crewAssignmentsQuery.data ?? [],
      });
    }, 180);
    return () => window.clearTimeout(timer);
  }, [
    crewAssignmentsQuery.data,
    crewQuery.data,
    conflictsQuery.data,
    detailQueriesSettled,
    equipmentQuery.data,
    hero,
    heroDetailQuery.data,
  ]);

  const currentDetails = detailSnapshot?.heroId === hero?.id ? detailSnapshot : null;
  const heroDetail = currentDetails?.heroDetail;
  const conflicts = currentDetails?.conflicts;
  const equipment = currentDetails?.equipment;
  const crew = currentDetails?.crew;
  const crewAssignments = useMemo(
    () => currentDetails?.crewAssignments ?? [],
    [currentDetails?.crewAssignments],
  );

  const riderStatusById = useMemo(
    () => new Map(riders.map((r) => [r.id, r.status])),
    [riders],
  );

  const stageCoverage = useMemo(() => {
    if (!heroDetail) return [];
    // Venues are crew locations, not lineup stages — no coverage card.
    return heroDetail.stages
      .filter((stage) => stage.stage_type !== "venue")
      .map((stage) => {
      const withRider = stage.slots.filter((s) => s.rider_id);
      const approved = withRider.filter(
        (s) => riderStatusById.get(s.rider_id!) === "approved",
      ).length;
      const timed = stage.slots.filter((s) => s.start_time && s.end_time).length;
      return {
        id: stage.id,
        name: stage.name,
        color: resolveStageColor(stage),
        approved,
        total: withRider.length,
        timed,
        slots: stage.slots.length,
      };
    });
  }, [heroDetail, riderStatusById]);

  const reviewedTotal = stageCoverage.reduce((n, s) => n + s.approved, 0);
  const ridersTotal = stageCoverage.reduce((n, s) => n + s.total, 0);
  const reviewMissing = Math.max(ridersTotal - reviewedTotal, 0);
  const stageCount = stageCoverage.length || (hero?.stage_count ?? 0);
  const slotsTotal =
    heroDetail?.stages.reduce((n, stage) => n + stage.slots.length, 0) ?? 0;
  const timedSlots = stageCoverage.reduce((n, stage) => n + stage.timed, 0);
  const missingTimes = Math.max(slotsTotal - timedSlots, 0);

  const reviewCount = riders.filter((r) => r.status === "extracted").length;
  const extractingCount = riders.filter((r) => r.status === "extracting").length;
  const unresolvedConflicts = (conflicts ?? []).filter((c) => !c.resolved);
  const criticalConflicts = unresolvedConflicts.filter(
    (c) => c.severity === "critical",
  ).length;
  const extractingNow = riders.find((r) => r.status === "extracting") ?? null;

  const assignedCrew = useMemo(
    () => new Set(crewAssignments.map((a) => a.crew_member_id)).size,
    [crewAssignments],
  );
  const requiredCrew = crew?.total_crew ?? 0;
  const crewGap = Math.max(requiredCrew - assignedCrew, 0);
  const crewCoverage =
    requiredCrew > 0 ? clamp01(assignedCrew / requiredCrew) : assignedCrew > 0 ? 1 : 0;

  const readinessMetrics = useMemo(() => {
    const riderScore =
      ridersTotal > 0
        ? reviewedTotal / ridersTotal
        : riders.length > 0
          ? riders.filter((r) => r.status === "approved").length / riders.length
          : 0;
    const timetableScore = slotsTotal > 0 ? timedSlots / slotsTotal : 0;
    const conflictScore = conflictsAllowed
      ? unresolvedConflicts.length === 0
        ? 1
        : clamp01(1 - unresolvedConflicts.length / Math.max(slotsTotal, 10))
      : null;
    const crewScore =
      crewAllowed && crew
        ? requiredCrew > 0
          ? crewCoverage
          : 1
        : null;

    return [
      {
        key: "riders",
        label: "Riders reviewed",
        value:
          ridersTotal > 0 ? `${reviewedTotal}/${ridersTotal}` : `${riders.length}`,
        detail:
          reviewMissing > 0
            ? `${reviewMissing} missing`
            : ridersTotal > 0
              ? "ready"
              : "not linked",
        score: riderScore,
        to: reviewMissing > 0 ? "/riders?status=extracted" : "/riders",
      },
      {
        key: "timetable",
        label: "Timed sets",
        value: slotsTotal > 0 ? `${timedSlots}/${slotsTotal}` : "0",
        detail:
          missingTimes > 0
            ? `${missingTimes} missing time`
            : slotsTotal > 0
              ? "timed"
              : "empty",
        score: timetableScore,
        to: hero ? `/festivals/${hero.id}?tab=grid` : "/festivals",
      },
      {
        key: "conflicts",
        label: "Conflicts",
        value: conflictsAllowed ? `${unresolvedConflicts.length}` : "Off",
        detail:
          !conflictsAllowed
            ? "not checked"
            : unresolvedConflicts.length > 0
              ? criticalConflicts > 0
                ? `${criticalConflicts} critical`
                : "open"
              : "clear",
        score: conflictScore,
        to: hero ? `/festivals/${hero.id}?tab=conflicts` : "/festivals",
      },
      {
        key: "crew",
        label: "Crew coverage",
        value: crew ? `${assignedCrew}/${requiredCrew}` : "Plan",
        detail:
          crew && requiredCrew > 0
            ? crewGap > 0
              ? `${crewGap} short`
              : "covered"
            : "open plan",
        score: crewScore,
        to: hero ? `/festivals/${hero.id}?tab=crew` : "/festivals",
      },
    ];
  }, [
    assignedCrew,
    conflictsAllowed,
    crew,
    crewAllowed,
    crewCoverage,
    crewGap,
    criticalConflicts,
    hero,
    missingTimes,
    requiredCrew,
    reviewMissing,
    reviewedTotal,
    riders,
    ridersTotal,
    slotsTotal,
    timedSlots,
    unresolvedConflicts.length,
  ]);

  const scoredReadiness = readinessMetrics.filter((m) => m.score !== null);
  const readiness =
    scoredReadiness.length > 0
      ? scoredReadiness.reduce((n, m) => n + (m.score ?? 0), 0) /
        scoredReadiness.length
      : 0;
  const readinessTone = toneForScore(readiness);
  const readinessColor = TONE_COLOR[readinessTone];

  const attention = useMemo(() => {
    const items: {
      key: string;
      icon: LucideIcon;
      color: string;
      pill: string;
      title: string;
      desc: string;
      to: string;
    }[] = [];

    const sorted = [...unresolvedConflicts].sort((a, b) =>
      a.severity === b.severity ? 0 : a.severity === "critical" ? -1 : 1,
    );
    for (const c of sorted.slice(0, 2)) {
      items.push({
        key: `c-${c.id}`,
        icon: AlertTriangle,
        color: c.severity === "critical" ? "var(--tr-bad)" : "var(--tr-warn)",
        pill: c.severity === "critical" ? "CRITICAL" : "REVIEW",
        title: `${c.conflict_type.replace(/_/g, " ")}${c.stage_name ? ` - ${c.stage_name}` : ""}`,
        desc: c.description,
        to: hero ? `/festivals/${hero.id}?tab=conflicts` : "/festivals",
      });
    }

    const toReview = riders
      .filter((r) => r.status === "extracted")
      .slice(0, 4 - items.length);
    for (const r of toReview) {
      items.push({
        key: `r-${r.id}`,
        icon: FileText,
        color: "var(--tr-warn)",
        pill: "REVIEW",
        title: `Review ${r.artist_name}`,
        desc: "Extraction is ready for a human check.",
        to: `/riders/${r.id}/review`,
      });
    }

    if (items.length < 4 && missingTimes > 0 && hero) {
      items.push({
        key: "missing-times",
        icon: Calendar,
        color: "var(--tr-warn)",
        pill: "TIME",
        title: `${missingTimes} sets missing time`,
        desc: "Add start and end times before production export.",
        to: `/festivals/${hero.id}?tab=grid`,
      });
    }

    if (items.length < 4 && crewGap > 0 && hero) {
      items.push({
        key: "crew-gap",
        icon: Users,
        color: "var(--tr-info)",
        pill: "CREW",
        title: `${crewGap} crew still needed`,
        desc: `${assignedCrew} assigned, ${requiredCrew} estimated.`,
        to: `/festivals/${hero.id}?tab=crew`,
      });
    }

    return items.slice(0, 4);
  }, [
    assignedCrew,
    crewGap,
    hero,
    missingTimes,
    requiredCrew,
    riders,
    unresolvedConflicts,
  ]);

  const topStats: {
    icon: LucideIcon;
    label: string;
    value: number;
    color: string;
    to: string;
  }[] = [
    {
      icon: FileText,
      label: "Need review",
      value: reviewCount,
      color: "var(--tr-warn)",
      to: "/riders?status=extracted",
    },
    conflictsAllowed && hero
      ? {
          icon: AlertTriangle,
          label: "Conflicts",
          value: unresolvedConflicts.length,
          color:
            unresolvedConflicts.length > 0 ? "var(--tr-bad)" : "var(--tr-ok)",
          to: `/festivals/${hero.id}?tab=conflicts`,
        }
      : {
          icon: Calendar,
          label: "Festivals",
          value: festivals.length,
          color: "var(--tr-accent)",
          to: "/festivals",
        },
    {
      icon: Upload,
      label: "Extracting",
      value: extractingCount,
      color: "var(--tr-info)",
      to: "/riders?status=extracting",
    },
    equipment && hero
      ? {
          icon: Boxes,
          label: "Rental items",
          value: equipment.total_items,
          color: "var(--tr-stage-stone)",
          to: `/festivals/${hero.id}?tab=equipment`,
        }
      : {
          icon: FileText,
          label: "Riders",
          value: riders.length,
          color: "var(--tr-stage-stone)",
          to: "/riders",
        },
  ];

  const moduleLinks: {
    key: string;
    icon: LucideIcon;
    title: string;
    metric: string;
    detail: string;
    to: string;
  }[] = [
    {
      key: "schedule",
      icon: Calendar,
      title: "Schedule",
      metric: `${stageCount} stage${stageCount === 1 ? "" : "s"}`,
      detail:
        slotsTotal > 0
          ? `${slotsTotal} set${slotsTotal === 1 ? "" : "s"} in timetable`
          : "Create stages and slots",
      to: hero ? `/festivals/${hero.id}?tab=grid` : "/festivals",
    },
    {
      key: "equipment",
      icon: Boxes,
      title: "Equipment",
      metric: equipment ? `${equipment.total_items}` : "Open",
      detail: equipment ? "rental items to confirm" : "Build the pull sheet",
      to: hero ? `/festivals/${hero.id}?tab=equipment` : "/festivals",
    },
    {
      key: "crew",
      icon: Users,
      title: "Crew",
      metric: crew ? `${assignedCrew}/${requiredCrew}` : "Plan",
      detail:
        crew && requiredCrew > 0
          ? crewGap > 0
            ? `${crewGap} people short`
            : "Covered"
          : "Assign crew and shifts",
      to: hero ? `/festivals/${hero.id}?tab=crew` : "/festivals",
    },
    {
      key: "logistics",
      icon: Route,
      title: "Logistics",
      metric: "Hotels + transport",
      detail: "Rooms, pickups, contacts",
      to: hero ? `/festivals/${hero.id}?tab=logistics` : "/festivals",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-bold" data-ds-display>
            {greeting()}, <span className="text-ink-3">{firstName}</span>
          </h1>
          <p className="mt-1 text-sm text-ink-3">
            {hero
              ? `${hero.name}: ${pct(readiness)}% ready. ${attention.length > 0 ? `${attention.length} item${attention.length === 1 ? "" : "s"} need attention.` : "No urgent action."}`
              : "Create a festival to start planning."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {layoutSwitch}
          <Link
            to="/learn"
            className="btn btn--ghost"
          >
            <PlayCircle className="h-3.5 w-3.5" strokeWidth={1.7} />
            Explore Trace
          </Link>
          <Link to="/festivals" className="btn btn--ghost">
            <Calendar className="h-3.5 w-3.5" strokeWidth={1.7} />
            New festival
          </Link>
          <Link to="/riders/upload" className="btn btn--accent">
            <Upload className="h-3.5 w-3.5" strokeWidth={1.7} />
            Upload rider
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 space-y-6">
          {hero ? (
            <section className="card overflow-hidden">
              <div className="grid gap-px bg-line-2 lg:grid-cols-[320px_minmax(0,1fr)]">
                <Link
                  to={`/festivals/${hero.id}`}
                  className="flex flex-col justify-between bg-panel p-5 transition-colors hover:bg-panel-2"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-4">
                          Next festival
                        </p>
                        <h2 className="mt-2 truncate text-xl font-bold text-ink" data-ds-display>
                          {hero.name}
                        </h2>
                      </div>
                      {heroLive ? (
                        <span
                          className="rounded-pill px-3 py-1 text-xs font-bold uppercase tracking-wide text-ok"
                          style={{
                            background:
                              "color-mix(in oklab, var(--tr-ok) 14%, transparent)",
                          }}
                        >
                          Live
                        </span>
                      ) : (
                        <span className="shrink-0 text-right">
                          <span className="block font-mono text-3xl font-bold tabular-nums text-accent">
                            {Math.max(daysUntil(hero.start_date), 0)}
                          </span>
                          <span className="block text-[10px] font-semibold uppercase tracking-wider text-ink-4">
                            days
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-ink-3">
                      <span className="flex items-center gap-1.5 font-mono text-xs tabular-nums">
                        <Calendar className="h-3.5 w-3.5 text-ink-4" strokeWidth={1.7} />
                        {hero.start_date} - {hero.end_date}
                      </span>
                      {hero.location && (
                        <span className="flex items-center gap-1.5 text-xs">
                          <MapPin className="h-3.5 w-3.5 text-ink-4" strokeWidth={1.7} />
                          {hero.location}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-3 gap-2">
                    {topStats.slice(0, 3).map((s) => (
                      <span key={s.label} className="rounded-ds-sm bg-panel-3 px-2.5 py-2">
                        <span
                          className="block font-mono text-lg font-bold tabular-nums"
                          style={{
                            color:
                              s.value > 0 &&
                              (s.label === "Conflicts" ||
                                s.label === "Need review")
                                ? s.color
                                : "var(--tr-text)",
                          }}
                        >
                          {s.value}
                        </span>
                        <span className="block truncate text-[10.5px] font-medium text-ink-3">
                          {s.label}
                        </span>
                      </span>
                    ))}
                  </div>
                </Link>

                <div className="bg-panel p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-4">
                        Readiness
                      </p>
                      <p className="mt-1 text-sm text-ink-3">
                        Riders, timetable, conflicts and crew in one score.
                      </p>
                    </div>
                    <div className="flex rounded-ds-sm bg-panel-3 p-1">
                      {(["readiness", "stages"] as const).map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setChartView(value)}
                          className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                            chartView === value
                              ? "bg-panel text-ink shadow-sm"
                              : "text-ink-3 hover:text-ink"
                          }`}
                        >
                          {value === "readiness" ? "Score" : "Stages"}
                        </button>
                      ))}
                    </div>
                  </div>

                  {chartView === "readiness" ? (
                    <div className="mt-5 grid gap-5 md:grid-cols-[150px_minmax(0,1fr)]">
                      <ReadinessRing
                        score={pct(readiness)}
                        color={readinessColor}
                      />
                      <div className="grid gap-2">
                        {readinessMetrics.map((metric) => {
                          const score = metric.score;
                          const color =
                            score === null
                              ? TONE_COLOR.neutral
                              : TONE_COLOR[toneForScore(score)];
                          return (
                            <Link
                              key={metric.key}
                              to={metric.to}
                              className="group rounded-ds-sm border border-line-2 bg-panel-2 px-3 py-2 transition-colors hover:border-line-3"
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-ink">
                                    {metric.label}
                                  </p>
                                  <p className="truncate text-xs text-ink-3">
                                    {metric.detail}
                                  </p>
                                </div>
                                <span className="font-mono text-sm font-bold tabular-nums text-ink">
                                  {metric.value}
                                </span>
                              </div>
                              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-panel-3">
                                <div
                                  className="h-full rounded-full"
                                  style={{
                                    width: `${score === null ? 0 : pct(score)}%`,
                                    background: color,
                                  }}
                                />
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <StageReviewChart stages={stageCoverage} festivalId={hero.id} />
                  )}
                </div>
              </div>
            </section>
          ) : (
            <div className="flex flex-col items-center rounded-ds border border-dashed border-line-3 px-6 py-14">
              <Calendar className="h-8 w-8 text-ink-4" strokeWidth={1.6} />
              <p className="mt-3 text-sm text-ink-3">No festivals yet</p>
              <Link to="/festivals" className="mt-2 text-sm text-accent hover:underline">
                Create your first festival
              </Link>
            </div>
          )}

          <section className="card overflow-hidden">
            <div className="flex items-center justify-between border-b border-line-2 px-4 py-3">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">
                Needs action
              </h3>
              <Link
                to={attention[0]?.to ?? "/riders"}
                className="flex items-center gap-1 text-xs text-ink-3 transition-colors hover:text-ink"
              >
                Open first <ArrowRight className="h-3 w-3" strokeWidth={1.6} />
              </Link>
            </div>
            {attention.length === 0 ? (
              <div className="flex items-center gap-2 px-4 py-6 text-sm text-ink-3">
                <CheckCircle2 className="h-4 w-4 text-ok" strokeWidth={1.8} />
                No urgent action for the selected festival.
              </div>
            ) : (
              attention.map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  className="group flex items-center gap-3 border-b border-line-2 px-4 py-3 transition-colors last:border-b-0 hover:bg-panel-2"
                >
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-ds-sm"
                    style={{
                      background: `color-mix(in oklab, ${item.color} 13%, transparent)`,
                    }}
                  >
                    <item.icon
                      className="h-4 w-4"
                      style={{ color: item.color }}
                      strokeWidth={1.8}
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-semibold capitalize text-ink">
                        {item.title}
                      </span>
                      <span
                        className="rounded-pill px-2 py-0.5 text-[9.5px] font-bold tracking-wide"
                        style={{
                          color: item.color,
                          background: `color-mix(in oklab, ${item.color} 14%, transparent)`,
                        }}
                      >
                        {item.pill}
                      </span>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-ink-3">{item.desc}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-ink-4 opacity-0 transition-opacity group-hover:opacity-100" strokeWidth={1.7} />
                </Link>
              ))
            )}
          </section>
        </div>

        <aside className="space-y-4">
          <DashboardHeroUpload compact />

          {extractingNow && (
            <div className="card p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-ink">
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-info" />
                  <span className="truncate">{extractingNow.artist_name}</span>
                </span>
                <span className="status-badge st-processing shrink-0">
                  <i />
                  Processing
                </span>
              </div>
              <p className="mt-1.5 font-mono text-[11px] text-ink-3">
                Extracting equipment and hospitality...
              </p>
              <div className="pbar mt-2.5">
                <div
                  className="pbar__fill animate-pulse"
                  style={
                    {
                      width: "65%",
                      background: "var(--tr-info)",
                    } as CSSProperties
                  }
                />
              </div>
            </div>
          )}

          <CrewCoverageCard
            assigned={assignedCrew}
            required={requiredCrew}
            gap={crewGap}
            coverage={crewCoverage}
            to={hero ? `/festivals/${hero.id}?tab=crew` : "/festivals"}
            available={!!crew}
          />

          <div className="card overflow-hidden">
            <div className="border-b border-line-2 px-4 py-3">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">
                Work areas
              </h3>
            </div>
            <div className="divide-y divide-line-2">
              {moduleLinks.map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-panel-2"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-ds-sm bg-panel-3 text-ink-3">
                    <item.icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-3">
                      <span className="text-sm font-semibold text-ink">
                        {item.title}
                      </span>
                      <span className="truncate font-mono text-[11px] tabular-nums text-ink-3">
                        {item.metric}
                      </span>
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-ink-3">
                      {item.detail}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-ink-4 opacity-0 transition-opacity group-hover:opacity-100" strokeWidth={1.7} />
                </Link>
              ))}
            </div>
          </div>

          <Link
            to="/learn"
            className="card flex w-full items-center gap-3 p-4 text-left transition-colors hover:border-line-3"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-ds-sm bg-accent-soft text-accent">
              <PlayCircle className="h-4 w-4" strokeWidth={1.7} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-ink">
                Explore Trace
              </span>
              <span className="mt-0.5 block text-xs text-ink-3">
                Try every feature with interactive sample data.
              </span>
            </span>
          </Link>
        </aside>
      </div>
    </div>
  );
}

function ReadinessRing({ score, color }: { score: number; color: string }) {
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - score / 100);

  return (
    <div className="flex items-center justify-center">
      <div className="relative h-[140px] w-[140px]">
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="var(--tr-border)"
            strokeWidth="10"
          />
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-mono text-3xl font-bold tabular-nums text-ink">
            {score}%
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-4">
            ready
          </span>
        </div>
      </div>
    </div>
  );
}

function StageReviewChart({
  stages,
  festivalId,
}: {
  stages: {
    id: string;
    name: string;
    color: string;
    approved: number;
    total: number;
    timed: number;
    slots: number;
  }[];
  festivalId: string;
}) {
  if (stages.length === 0) {
    return (
      <div className="mt-5 rounded-ds-sm border border-dashed border-line-3 px-4 py-8 text-center text-sm text-ink-3">
        Add stages and linked riders to see review coverage.
      </div>
    );
  }

  return (
    <div className="mt-5 space-y-3">
      {stages.map((stage) => {
        const reviewPct = stage.total > 0 ? stage.approved / stage.total : 0;
        const timePct = stage.slots > 0 ? stage.timed / stage.slots : 0;
        return (
          <Link
            key={stage.id}
            to={`/festivals/${festivalId}?tab=grid`}
            className="block rounded-ds-sm border border-line-2 bg-panel-2 px-3 py-2"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate text-sm font-semibold text-ink">
                {stage.name}
              </span>
              <span className="font-mono text-xs tabular-nums text-ink-3">
                {stage.total > 0 ? `${stage.approved}/${stage.total}` : "no riders"}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-[72px_minmax(0,1fr)] items-center gap-2">
              <span className="text-[10.5px] font-medium text-ink-4">Review</span>
              <div className="h-1.5 overflow-hidden rounded-full bg-panel-3">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${pct(reviewPct)}%`,
                    background: stage.color,
                  }}
                />
              </div>
              <span className="text-[10.5px] font-medium text-ink-4">Times</span>
              <div className="h-1.5 overflow-hidden rounded-full bg-panel-3">
                <div
                  className="h-full rounded-full bg-info"
                  style={{ width: `${pct(timePct)}%` }}
                />
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function CrewCoverageCard({
  assigned,
  required,
  gap,
  coverage,
  to,
  available,
}: {
  assigned: number;
  required: number;
  gap: number;
  coverage: number;
  to: string;
  available: boolean;
}) {
  const color = gap > 0 ? "var(--tr-warn)" : "var(--tr-ok)";

  return (
    <Link to={to} className="card block p-4 transition-colors hover:border-line-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-4">
            Crew coverage
          </p>
          <p className="mt-1 text-sm font-semibold text-ink">
            {available
              ? gap > 0
                ? `${gap} people short`
                : "Crew covered"
              : "Crew not planned"}
          </p>
        </div>
        <Users className="h-4 w-4 text-ink-3" strokeWidth={1.7} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <p className="font-mono text-2xl font-bold tabular-nums text-ink">
            {assigned}
          </p>
          <p className="text-xs text-ink-3">assigned</p>
        </div>
        <div>
          <p className="font-mono text-2xl font-bold tabular-nums text-ink">
            {required}
          </p>
          <p className="text-xs text-ink-3">needed</p>
        </div>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-panel-3">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct(coverage)}%`, background: color }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-ink-3">
        <span>{available ? `${pct(coverage)}% covered` : "Open crew tab"}</span>
        <span className="inline-flex items-center gap-1 text-ink-2">
          Open <ChevronRight className="h-3 w-3" strokeWidth={1.7} />
        </span>
      </div>
    </Link>
  );
}
